import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Users, Pencil, Trash2, Linkedin } from 'lucide-react';
import type { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { useProject } from '../../../contexts/ProjectContext';
import { contentApi, type Author } from '../../../api/content';
import {
  Button,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Input,
  Card,
  EmptyState,
} from '../../../components/ui';
import { Avatar } from '../../../components/shared/Avatar';
import { DataTable, DataTableToolbar, useDataTablePrefs } from '../../../components/shared/DataTable';
import { ListPageLayout } from '../../../components/layout';

const ALL_COLUMNS = [
  { id: 'name', label: 'Author' },
  { id: 'role', label: 'Role' },
  { id: 'email', label: 'Email' },
  { id: 'linkedin', label: 'LinkedIn' },
  { id: 'actions', label: 'Actions' },
];

const DEFAULT_VISIBLE = ['name', 'role', 'email', 'linkedin', 'actions'];

const AuthorsPage = () => {
  const navigate = useNavigate();
  const { currentProject, buildProjectUrl } = useProject();
  const workspaceId = currentProject?.workspaceId;
  const [authors, setAuthors] = useState<Author[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('');
  const [search, setSearch] = useState('');

  const { prefs, toggleColumn, setDensity, setSorting } = useDataTablePrefs('authors', DEFAULT_VISIBLE);

  const fetchAuthors = useCallback(() => {
    if (!workspaceId) return;
    setLoading(true);
    contentApi.listAuthors(workspaceId)
      .then(setAuthors)
      .catch(() => setAuthors([]))
      .finally(() => setLoading(false));
  }, [workspaceId]);

  useEffect(() => { fetchAuthors(); }, [fetchAuthors]);

  const handleCreate = async () => {
    if (!workspaceId || !newName.trim()) return;
    try {
      await contentApi.createAuthor(workspaceId, {
        name: newName.trim(),
        email: newEmail.trim() || undefined,
        role: newRole.trim() || undefined,
      } as Partial<Author>);
      setNewName('');
      setNewEmail('');
      setNewRole('');
      setShowCreate(false);
      fetchAuthors();
    } catch (err) {
      console.error('Failed to create author', err);
    }
  };

  const handleDelete = async (authorId: string) => {
    if (!workspaceId) return;
    if (!window.confirm('Delete this author?')) return;
    try {
      await contentApi.deleteAuthor(workspaceId, authorId);
      fetchAuthors();
    } catch (err) {
      console.error('Failed to delete author', err);
    }
  };

  const closeModal = () => {
    setShowCreate(false);
    setNewName('');
    setNewEmail('');
    setNewRole('');
  };

  const columns = useMemo<ColumnDef<Author, any>[]>(() => [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Author',
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <Avatar name={row.original.name} size="sm" />
          <button
            onClick={e => { e.stopPropagation(); navigate(buildProjectUrl(`au/${row.original.id}`, 'sm')); }}
            className="text-sm font-medium text-neutral-800 hover:text-coral-600 transition-colors truncate"
          >
            {row.original.name}
          </button>
        </div>
      ),
      enableSorting: true,
    },
    {
      id: 'role',
      accessorKey: 'role',
      header: 'Role',
      cell: ({ getValue }) => (
        <span className="text-xs text-neutral-500">{(getValue() as string) || '\u2014'}</span>
      ),
      enableSorting: true,
    },
    {
      id: 'email',
      accessorKey: 'email',
      header: 'Email',
      cell: ({ getValue }) => (
        <span className="text-xs text-neutral-500 truncate block max-w-[200px]">{(getValue() as string) || '\u2014'}</span>
      ),
      enableSorting: true,
    },
    {
      id: 'linkedin',
      accessorKey: 'linkedin_profile_url',
      header: 'LinkedIn',
      cell: ({ getValue }) => {
        const url = getValue() as string | undefined;
        return url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-500 hover:text-blue-600 flex items-center gap-1 text-xs"
            onClick={e => e.stopPropagation()}
          >
            <Linkedin size={12} /> Connected
          </a>
        ) : (
          <span className="text-neutral-400 text-xs">Not linked</span>
        );
      },
      enableSorting: false,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(buildProjectUrl(`au/${row.original.id}`, 'sm'))}
            className="!px-1.5"
          >
            <Pencil size={12} />
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => handleDelete(row.original.id)}
            className="!px-1.5"
          >
            <Trash2 size={12} />
          </Button>
        </div>
      ),
      enableSorting: false,
      size: 80,
    },
  ], [navigate, buildProjectUrl, handleDelete]);

  const columnVisibility = useMemo<VisibilityState>(() => {
    const vis: VisibilityState = {};
    ALL_COLUMNS.forEach(c => { vis[c.id] = prefs.visibleColumns.includes(c.id); });
    return vis;
  }, [prefs.visibleColumns]);

  return (
    <ListPageLayout
      title="Contributors"
      subtitle="Manage content authors and their writing profiles"
      icon={<Users size={20} />}
      actions={
        <Button
          variant="default"
          leftIcon={<Plus size={14} />}
          onClick={() => setShowCreate(true)}
        >
          Add Author
        </Button>
      }
      toolbar={
        <DataTableToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search authors..."
          allColumns={ALL_COLUMNS}
          visibleColumns={prefs.visibleColumns}
          onToggleColumn={toggleColumn}
          density={prefs.density}
          onDensityChange={setDensity}
        />
      }
    >
      {/* Create author modal */}
      <Modal open={showCreate} onOpenChange={(o) => { if (!o) closeModal(); }} size="md">
        <ModalHeader title="New Author" subtitle="Add a new content contributor" />
        <ModalBody className="space-y-4">
          <Input
            label="Name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Full name"
            autoFocus
          />
          <Input
            label="Email"
            type="email"
            value={newEmail}
            onChange={e => setNewEmail(e.target.value)}
            placeholder="Email address"
          />
          <Input
            label="Role"
            value={newRole}
            onChange={e => setNewRole(e.target.value)}
            placeholder="e.g., CEO, CTO"
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="default" onClick={handleCreate} disabled={!newName.trim()}>
            Create
          </Button>
        </ModalFooter>
      </Modal>

      {/* Authors table */}
      {!loading && authors.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Users size={48} strokeWidth={1.5} />}
            title="No authors yet"
            description="Add your first author to get started."
            action={
              <Button variant="default" leftIcon={<Plus size={14} />} onClick={() => setShowCreate(true)}>
                Add Author
              </Button>
            }
          />
        </Card>
      ) : (
        <DataTable<Author>
          data={authors}
          columns={columns}
          sorting={prefs.sorting}
          onSortingChange={setSorting}
          columnVisibility={columnVisibility}
          globalFilter={search}
          density={prefs.density}
          isLoading={loading}
          getRowId={row => row.id}
          onRowClick={row => navigate(buildProjectUrl(`au/${row.id}`, 'sm'))}
          emptyState={
            <p className="text-center text-sm text-neutral-400">No authors match your search</p>
          }
        />
      )}
    </ListPageLayout>
  );
};

export default AuthorsPage;
